import React from "react";
import ReactDOM from "react-dom/client";
import {
    BrowserRouter,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { YbugProvider } from "ybug-react";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";

import { logEvent } from "firebase/analytics";
import { Provider } from "react-redux";

import { Toaster } from "react-hot-toast";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./lib/pdfWorker";
import App from "./App";
import "./global-styles/main.scss";
// Import externals so it can be built too.
import "./externals";
import { analytics } from "./firebase";
import AppFallback from "./components/ErrorBoundaryFallback/AppFallback/AppFallback";
import AuthProvider from "./context/auth/AuthProvider";
import { store } from "./redux/store";
import { initPracticeSessionSync } from "./utils/practiceSessionStore";
import { filterSentryClientNoise } from "./utils/sentryClientNoiseFilter";

logEvent(analytics, "frontend-analytics");

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    ignoreErrors: ["Permission denied"],
    replaysSessionSampleRate: import.meta.env.VITE_NODE_ENV !== "dev" ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    environment: import.meta.env.VITE_NODE_ENV,
    tracesSampleRate: 0.5,
    enabled: import.meta.env.VITE_NODE_ENV !== "dev",
    enableLogs: true,
    beforeSend: filterSentryClientNoise,
});

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnMount: import.meta.env.VITE_NODE_ENV !== "dev",
            refetchOnWindowFocus: import.meta.env.VITE_NODE_ENV !== "dev",
            refetchOnReconnect: import.meta.env.VITE_NODE_ENV !== "dev",
            retry: 1,
            // set staletime of all fetched data to 5mins
            staleTime:
                import.meta.env.VITE_NODE_ENV === "production"
                    ? 2 * 60 * 1000
                    : 30 * 60 * 1000,
        },
    },
});

initPracticeSessionSync();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <ErrorBoundary
            fallback={<AppFallback />}
            onError={(err) => {
                Sentry.captureException(err);
            }}
        >
            <AuthProvider>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <YbugProvider
                            ybugId={import.meta.env.VITE_YBUG_ID}
                            settings={{
                                launcher_position: "bottom-left",
                                rating: false,
                                email_required: true,
                            }}
                        >
                            <Provider store={store}>
                                <App />
                            </Provider>
                        </YbugProvider>
                        <Toaster />
                    </BrowserRouter>
                    <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
            </AuthProvider>
        </ErrorBoundary>
    </React.StrictMode>
);
